/* eslint-disable @typescript-eslint/no-explicit-any */
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getApp } from 'firebase/app'

import { AddEmployeeUserItem, StripePrice } from '@batical/reducer/src/Stripe'
import { CalculCodeStateKeys } from '@batical/types/src'

export async function retrieveStripePrice(priceId: Array<string>): Promise<Array<StripePrice>> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const priceFunction = httpsCallable<{ priceId: Array<string> }, Array<StripePrice>>(functions, 'retrieveStripePrice')
  const response = await priceFunction({ priceId })
  return response.data as Array<StripePrice>
}

export async function retrieveStripeIncomingInvoice(customerId: string): Promise<StripePrice> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const priceFunction = httpsCallable<{ customerId: string }, StripePrice>(functions, 'upcomingInvoice')
  const response = await priceFunction({ customerId })
  return response.data as StripePrice
}

export async function createCheckoutSession(
  priceId: string,
  baseUrl: string,
  idsList?: Array<AddEmployeeUserItem>,
  contentMail?: string
): Promise<{ url: string }> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const checkoutSession = httpsCallable<
    { priceId: string; baseUrl: string; idsList?: Array<AddEmployeeUserItem>; contentMail?: string },
    { url: string }
  >(functions, 'createCheckoutSession')
  const response = await checkoutSession({ priceId, baseUrl, idsList, contentMail })
  return response.data as { url: string }
}

export async function updateQuantitySubscription(
  subscriptionId: string,
  idsList?: Array<AddEmployeeUserItem>,
  contentMail?: string
): Promise<{ url: string }> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const checkoutSession = httpsCallable<
    { subscriptionId: string; idsList?: Array<AddEmployeeUserItem>; contentMail?: string },
    { url: string }
  >(functions, 'updateQuantitySubscription')
  const response = await checkoutSession({ subscriptionId, idsList, contentMail })
  return response.data as { url: string }
}

export async function createCustomerPortalSession(returnUrl: string): Promise<{ url: string }> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const callable = httpsCallable<{ baseUrl: string }, { url: string }>(functions, 'createCustomerPortalSession')
  const response = await callable({ baseUrl: returnUrl })
  return response.data as { url: string }
}

export async function generateMail(html: string, calculCode: CalculCodeStateKeys): Promise<any> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const functionCaller = httpsCallable<{ html: string; calculCode: CalculCodeStateKeys }, any>(functions, 'generateMail')
  const response = await functionCaller({ html, calculCode })
  return response.data
}

export async function removeOneSubscription(targetId: string, subscriptionId: string): Promise<any> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const functionCaller = httpsCallable<{ targetId: string; subscriptionId: string }, any>(functions, 'removeSubs')
  const response = await functionCaller({ targetId, subscriptionId })
  return response.data
}

/** Payload for createGroupCheckoutSession (batiweb team subscriptions) */
export type GroupSubscriber = {
  mail: string
  firstname?: string
  lastname?: string
  company?: string
}

export async function createGroupCheckoutSession(payload: {
  ownerUid: string
  baseUrl: string
  priceId: string
  subscribers: GroupSubscriber[]
}): Promise<{ url: string }> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const callable = httpsCallable<typeof payload, { url: string }>(functions, 'createGroupCheckoutSession')
  const response = await callable(payload)
  return response.data
}

/** Rejoue la synchro post–Checkout groupe (équivalent webhook) — utile si `groupCheckoutPending` reste orphelin. */
export async function finalizeGroupCheckoutSession(sessionId: string): Promise<{ success: boolean }> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const callable = httpsCallable<{ sessionId: string }, { success: boolean }>(
    functions,
    'finalizeGroupCheckoutSession'
  )
  const response = await callable({ sessionId })
  return response.data as { success: boolean }
}

/** Réponse `addSeatsToSubscription` (Cloud Function). */
export type AddSeatsResult = {
  success: boolean
  newQuantity: number
  seatsPurchased?: number
  seatsReused?: number
}

export async function addSeatsToSubscription(newSubscribers: GroupSubscriber[]): Promise<AddSeatsResult> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const callable = httpsCallable<{ newSubscribers: GroupSubscriber[] }, AddSeatsResult>(
    functions,
    'addSeatsToSubscription'
  )
  const response = await callable({ newSubscribers })
  return response.data as AddSeatsResult
}

/** Message utilisateur court après ajout de sièges. */
export function formatAddSeatsSummary(r: AddSeatsResult): string {
  const p = r.seatsPurchased ?? 0
  const u = r.seatsReused ?? 0
  if (p === 0 && u === 0) {
    return 'Aucun nouveau siège (emails déjà actifs ou doublons).'
  }
  return `${u} siège(s) réutilisé(s), ${p} acheté(s). Quantité : ${r.newQuantity}.`
}

/** Résultat de syncGroupSubscriptionFromStripe. */
export type SyncGroupResult = {
  synced: boolean
  reason?: string
  detail?: string
  stripeSubscriptionId?: string
  status?: string
  quantity?: number
  activeMembers?: number
  freeSeats?: number
  periodEnd?: number
}

/** Sync Firestore ← Stripe : relit l'état réel de l'abonnement et met à jour Firestore. */
export async function syncGroupSubscriptionFromStripe(): Promise<SyncGroupResult> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const callable = httpsCallable<void, SyncGroupResult>(functions, 'syncGroupSubscriptionFromStripe')
  const response = await callable()
  return response.data as SyncGroupResult
}

export async function removeSeatFromSubscription(
  subscriberMail: string
): Promise<{ success: boolean; newQuantity: number }> {
  const functions = getFunctions(getApp(), 'europe-west1')
  const callable = httpsCallable<{ subscriberMail: string }, { success: boolean; newQuantity: number }>(
    functions,
    'removeSeatFromSubscription'
  )
  const response = await callable({ subscriberMail })
  return response.data
}
